/**
 * KPIs grand public v0.2 (PRD §14.2–14.3) : majorité au Parlement (jauge en hémicycle), pouvoir d'achat (indice et
 * « ≈ X € par mois pour un ménage médian » si la baseline donne ce revenu), indicateurs tangibles des services
 * publics en phrases. Chaque bloc n'apparaît que si le moteur fournit la clé KPI (feature-detect, ADR 0006).
 */
import type { Baseline, Figure, Param, Source, YearState } from "../../../engine/src/types.js";
import type { V02Kpi } from "../engine-adapter.js";
import { escapeHtml as e, eur, hoursMinutes, int, num, signed } from "../format.js";
import type { Store } from "../state.js";
import { srcButton } from "./source-info.js";
import { pastille } from "./brand.js";

export interface TangibleDef {
  key: V02Kpi;
  /** Racine des identifiants de paramètres qui portent la relation (ⓘ). */
  stem: string;
  label: string;
  sentence: (v: number) => string;
  dec: number;
}

export const TANGIBLE_DEFS: TangibleDef[] = [
  { key: "er_wait_hours", stem: "er_wait", label: "Urgences", sentence: (v) => `${hoursMinutes(v)} de passage aux urgences (durée médiane)`, dec: 2 },
  { key: "rail_incidents_index", stem: "rail_incident", label: "Trains", sentence: (v) => `${num(v, 1)} % des trains de voyageurs en retard d'au moins 5 minutes`, dec: 1 },
  { key: "court_delay_months", stem: "court_delay", label: "Justice", sentence: (v) => `${num(v, 1)} mois de durée moyenne des affaires civiles (tribunaux judiciaires)`, dec: 1 },
  { key: "pupils_per_class", stem: "pupils_per_class", label: "École", sentence: (v) => `${num(v, 1)} élèves par classe en moyenne (premier degré public)`, dec: 1 },
  { key: "prison_occupancy_pct", stem: "prison_occupancy", label: "Prisons", sentence: (v) => `densité carcérale de ${int(v)} % (détenus pour 100 places)`, dec: 0 },
];

/** Paramètres dont l'identifiant contient la racine : ils portent la relation crédits → indicateur. */
export function relatedParams(store: Store, stem: string): Param[] {
  return Object.values(store.content.params).filter((p) => p.id.includes(stem));
}

export function paramsSourceButton(store: Store, title: string, stem: string, extraNotes: string[] = [], inline = true): string {
  const ps = relatedParams(store, stem);
  const sources: Array<Source | "TODO"> = ps.length ? ps.map((p) => p.source) : ["TODO"];
  const notes = ps.map((p) => `${p.label}${p.value !== null ? ` : ${num(p.value, 2)}` : ""}${p.low !== undefined && p.high !== undefined ? ` (fourchette ${num(p.low, 2)} – ${num(p.high, 2)})` : ""}${p.source === "TODO" ? " — à sourcer" : ""}.`);
  if (!ps.length) notes.push("Paramètres de la relation non encore publiés par le moteur : chiffre à sourcer.");
  return srcButton({ title, sources, status: "simulated", notes: [...notes, ...extraNotes] }, { inline });
}

/** Valeur de départ d'un KPI v0.2 : baseline (bloc `services` ou `macro`) ou paramètre `<clé>_ref` ; sinon undefined. */
export function startFigure(b: Baseline, store: Store, key: string): Figure | undefined {
  const services = (b as unknown as { services?: Record<string, Figure> }).services;
  const f = services?.[key] ?? b.macro[key] ?? b.politics[key];
  if (f && f.value !== null) return f;
  const p = store.content.params[`${key}_ref`] ?? store.content.params[`${key}_base`];
  return p && p.value !== null ? p : undefined;
}

/** Liste « au guichet » : indicateur de fin, comparé au départ (baseline ou première année simulée). */
export function tangibleList(store: Store, first: YearState | undefined, last: YearState): string {
  const rows = TANGIBLE_DEFS.filter((d) => last.kpis[d.key] !== undefined).map((d) => {
    const to = last.kpis[d.key]!;
    const sf = startFigure(store.content.baseline, store, d.key);
    const from = sf?.value ?? first?.kpis[d.key];
    const fromYear = sf ? store.content.baseline.meta.base_year : first?.year;
    const delta = from !== undefined && from !== null ? to - from : undefined;
    const eps = Math.pow(10, -d.dec) / 2;
    // Indicateurs « au guichet » : plus haut = moins bien (attente, retards, délais…), comme la règle d'origine.
    const effect = delta === undefined || Math.abs(delta) < eps ? "equal" : delta > 0 ? "worse" : "better";
    const pa = delta === undefined ? "" : pastille(effect, effect === "equal" ? 0 : delta, effect === "equal" ? "égal" : signed(delta, d.dec), "depuis le départ");
    return `<li class="tg"><b>${e(d.label)}</b>${paramsSourceButton(store, d.label, d.stem)}<br>
      ${pa} En ${last.year} : ${e(d.sentence(to))}${from !== undefined && from !== null && fromYear !== undefined ? `<br><span class="mut sm">${fromYear} : ${e(d.sentence(from))}</span>` : ""}</li>`;
  });
  return rows.length ? `<ul class="tglist">${rows.join("")}</ul>` : "";
}

/* ------------------------------------------------------------------ majorité */

/** Seuil de majorité : paramètre `parliament_majority_threshold` s'il existe, sinon la moitié de l'échelle 0–100 du contrat. */
export function majorityThreshold(store: Store): number {
  const p = store.content.params.parliament_majority_threshold?.value;
  return p ?? 100 / 2;
}

/** Jauge « hémicycle » : points remplis à proportion du soutien ; la moitié est marquée. */
export function majorityGauge(value: number, threshold: number, size: "s" | "m" = "m"): string {
  const rows = 5;
  const seats: Array<{ x: number; y: number; a: number }> = [];
  for (let r = 0; r < rows; r++) {
    const rad = 34 + r * 9;
    const n = Math.round((Math.PI * rad) / 9);
    for (let i = 0; i < n; i++) {
      const a = Math.PI - (i * Math.PI) / (n - 1);
      seats.push({ x: 80 + rad * Math.cos(a), y: 82 - rad * Math.sin(a), a });
    }
  }
  seats.sort((p, q) => q.a - p.a);
  const filled = Math.round((seats.length * Math.max(0, Math.min(100, value))) / 100);
  const ok = value > threshold;
  const dots = seats.map((s, i) => `<circle cx="${s.x.toFixed(1)}" cy="${s.y.toFixed(1)}" r="3.2" fill="${i < filled ? (ok ? "var(--up-fill)" : "var(--down-fill)") : "var(--line)"}"/>`).join("");
  const ta = Math.PI - (threshold / 100) * Math.PI;
  const tick = `<line x1="${(80 + 28 * Math.cos(ta)).toFixed(1)}" y1="${(82 - 28 * Math.sin(ta)).toFixed(1)}" x2="${(80 + 76 * Math.cos(ta)).toFixed(1)}" y2="${(82 - 76 * Math.sin(ta)).toFixed(1)}" stroke="var(--txt)" stroke-width="1" stroke-dasharray="2 2"/>`;
  return `<svg class="hemi ${size}" viewBox="0 0 160 88" role="img" aria-label="Soutien au Parlement ${int(value)} sur 100, seuil de majorité ${int(threshold)}">${dots}${tick}<text x="80" y="80" text-anchor="middle" font-size="18" font-weight="700" fill="var(--txt)">${int(value)}</text></svg>`;
}

export function majorityText(value: number, threshold: number): string {
  return value > threshold ? `Majorité : ${int(value)} % des sièges` : `Minorité : ${int(value)} % des sièges, censure possible`;
}

/* ------------------------------------------------------------------ pouvoir d'achat */

const INCOME_KEYS_MONTHLY = ["median_household_income_monthly", "median_disposable_income_monthly", "median_living_standard_monthly"];
const INCOME_KEYS_ANNUAL = ["median_household_income", "median_disposable_income", "median_living_standard"];

/** Revenu disponible mensuel d'un ménage médian, s'il est dans la baseline (macro). */
export function medianIncomeMonthly(b: Baseline): { value: number; fig: Figure } | undefined {
  for (const k of INCOME_KEYS_MONTHLY) {
    const f = b.macro[k];
    if (f?.value) return { value: f.value, fig: f };
  }
  for (const k of INCOME_KEYS_ANNUAL) {
    const f = b.macro[k];
    if (f?.value) return { value: f.value / 12, fig: f };
  }
  return undefined;
}

/** « ≈ +12 € par mois pour un ménage médian » (si revenu médian connu), sinon chaîne vide. */
export function ppEuros(b: Baseline, index: number): string {
  const inc = medianIncomeMonthly(b);
  if (!inc) return "";
  const d = (index / 100 - 1) * inc.value;
  return `≈ ${d >= 0 ? "+" : "−"}${eur(Math.abs(d))} par mois pour un ménage médian`;
}

export function ppLine(store: Store, index: number): string {
  const euros = ppEuros(store.content.baseline, index);
  return `indice ${num(index, 1)} (100 = ${store.content.baseline.meta.base_year})${euros ? ` · ${euros}` : ""} · ${signed(index - 100, 1)} pt`;
}
